import {
  ConfigurationField,
  ENVIRONMENTS,
  IAnnotation,
  RendererTypes,
} from "@bronte/viewer-api/src/main";
import { AnnotationTypes } from "./constants";
import { URLParams } from "./types";
import {
  DEVSHELL_EXPRESS_GATEWAY_DEFAULT,
  fetchCloudfrontSignedCookies,
  isLocalhostName,
  parseHostedTier,
  PI_CLIENT_ID_NON_PROD,
  PI_CLIENT_ID_PROD,
  resolvePiTier,
  sessionJsUrlForTier,
} from "./piLogin";

// The hosts and tiers live in piLogin.js, which every app (both shells, the EPUB reader,
// My Library, the comparator) logs in through.
export {
  DEVSHELL_EXPRESS_GATEWAY_DEFAULT,
  LOCAL_DEV_SHELL_VIEWER_HOSTNAME,
  LOCAL_STATIC_SERVER_HOSTNAME,
} from "./piLogin";

/** Tier segment from the launch hostname for hosted express shells (see DEVSHELL_EXPRESS_GATEWAY_DEFAULT shape). */
export type DevShellExpressLaunchTier = "dev" | "qaint" | "stg" | "ppe" | "prod";

/** Hosted express launch tier from `hostname` (piLogin.js parseHostedTier). */
export const parseDevShellExpressLaunchTier = (
  hostname: string,
): DevShellExpressLaunchTier | null => parseHostedTier(hostname);

/** Tier for PI session + viewer env: hosted express hostnames plus the local dev-shell hosts. */
export const getDevShellExpressResolvedTier = (
  hostname: string,
): DevShellExpressLaunchTier | null => resolvePiTier(hostname);

/** Viewer / LT env aligned with PI session for this hosted express tier. */
export function getEnvFromDevShellExpressTier(tier: DevShellExpressLaunchTier): ENVIRONMENTS {
  switch (tier) {
    case "dev":
    case "stg":
      return ENVIRONMENTS.STG;
    case "qaint":
      return ENVIRONMENTS.QA;
    case "ppe":
      return ENVIRONMENTS.PPE;
    case "prod":
      return ENVIRONMENTS.PROD;
  }
}

/** Pearson PI session.js for hosted express tier (product URLs; not launch origins). */
export const getSessionJsURLForDevShellExpressTier = (tier: DevShellExpressLaunchTier): string =>
  sessionJsUrlForTier(tier);

/**
 * Secure express package launch: `?exportId=…` without an explicit `packagePath` query param.
 * Non-secure: explicit `packagePath` query (direct asset host, no gateway cookie priming).
 */
export function usesSecureExpressPackageAccess(params: {
  exportId: string;
  securePackageAccess?: boolean;
}): boolean {
  if (params.securePackageAccess !== undefined) {
    return params.securePackageAccess;
  }
  const rawPackagePath = new URLSearchParams(window.location.search).get("packagePath")?.trim();
  if (rawPackagePath) {
    return false;
  }
  return Boolean(params.exportId);
}

export const getDevShellExpressGatewayBaseUrl = (): string => {
  const tier = parseDevShellExpressLaunchTier(window.location.hostname);
  if (tier !== null) {
    return window.location.origin.replace(/\/$/, "");
  }
  const fromEnv = process.env.VITE_DEVSHELL_EXPRESS_URL;
  return (fromEnv || DEVSHELL_EXPRESS_GATEWAY_DEFAULT).replace(/\/$/, "");
};

/** Prime CloudFront signed cookies on the express gateway (cookie auth for asset URLs). */
export const fetchCloudfrontSignedCookiesForExpress = (pearsonToken: string): Promise<Response> =>
  fetchCloudfrontSignedCookies(getDevShellExpressGatewayBaseUrl(), pearsonToken);

export const isLocalhost = (): boolean => isLocalhostName(window.location.hostname);

const isLocalhostOrHasPort = (): boolean => {
  return isLocalhost() || Boolean(window.location.port);
};

export const getURLParams = (renderer?: string): URLParams => {
  const pathname = window.location.pathname.split("/");
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);

  const resolvedTier = getDevShellExpressResolvedTier(window.location.hostname);
  const envFromHost =
    resolvedTier !== null ? getEnvFromDevShellExpressTier(resolvedTier) : undefined;

  const rawPackagePath = (urlParams.get("packagePath") ?? "").trim();
  const exportId = (urlParams.get("exportId") ?? "").trim();

  let packagePath = "";
  if (rawPackagePath) {
    packagePath = rawPackagePath;
  } else if (exportId) {
    const origin = window.location.origin.replace(/\/$/, "");

    if (isLocalhostOrHasPort()) {
      packagePath = `${origin}/courses/${encodeURIComponent(exportId)}`;
    } else {
      packagePath = `${origin}/${encodeURIComponent(exportId)}`;
    }
  }

  console.log("packagePath", packagePath);

  const securePackageAccess = Boolean(exportId) && !rawPackagePath;

  return {
    cohortId: pathname[1],
    deploymentId: pathname[2],
    env:
      urlParams.get("env") ||
      envFromHost ||
      (renderer !== RendererTypes.EXPRESS ? ENVIRONMENTS.QA : ENVIRONMENTS.STG),
    pocEnabled: urlParams.get("pocEnabled") || "",
    disableIES: urlParams.get("disableIES") || "",
    screenId: urlParams.get("screenId") || "",
    userId: urlParams.get("userId") || "",
    token: urlParams.get("token") || "",
    exportId,
    packagePath,
    securePackageAccess,
    minimalShell: urlParams.get("minimalShell") === "true" || process.env.VITE_BV_UAT === "true",
  };
};

/**
 * Reflects the currently rendered screen in the address bar so a reload or copied URL lands
 * back on the same screen (via resolveInitialRoute). replaceState: nothing listens to
 * popstate, so pushed entries would make the back button cycle through dead URLs.
 */
export const syncScreenIdUrlParam = (screenId: string): void => {
  const url = new URL(window.location.href);
  if (url.searchParams.get("screenId") === screenId) {
    return;
  }
  url.searchParams.set("screenId", screenId);
  window.history.replaceState(window.history.state, "", url);
};

export const getAuthToken = (callback: (token: string) => void): void => {
  let token = getURLParams().token;

  if (!token) {
    window.piSession?.getToken(async (_status: string, pearsonToken: string) => {
      token = pearsonToken;
    });
  }

  callback(token);
};

export const getClientId = (): string => {
  return getURLParams().env === ENVIRONMENTS.PROD ? PI_CLIENT_ID_PROD : PI_CLIENT_ID_NON_PROD;
};

export const getSessionJsURL = (): string => {
  const resolvedTier = getDevShellExpressResolvedTier(window.location.hostname);
  if (resolvedTier !== null) {
    return getSessionJsURLForDevShellExpressTier(resolvedTier);
  }

  const renderer = process.env.VITE_BV_RENDERER;
  switch (getURLParams(renderer).env) {
    case ENVIRONMENTS.PPE:
      return sessionJsUrlForTier("ppe");
    case ENVIRONMENTS.PROD:
      return sessionJsUrlForTier("prod");
    case ENVIRONMENTS.QA:
      return sessionJsUrlForTier("qaint");
    default:
      return sessionJsUrlForTier("stg");
  }
};

export const getTitle = (data: ConfigurationField[]): string =>
  data.find(({ fieldName }) => fieldName === "title")?.fieldValue || "";

// generate and return unique id when adding new notes/highlight
export const getUniqueIdForAnnotation = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

// Check if annotation is flash card
export const isFlashCard = (annotation: IAnnotation): boolean => {
  return annotation?.annotationType?.toLowerCase() === AnnotationTypes.FlashCard.toLowerCase();
};
